// Search-migration E4 (FR23 / Decision URL) — a TypeScript port of the Jmix
// `service.search.global.SearchFilters` URL serialization (`fromUrlParams` / `toQueryString`). The
// results page mirrors its full filter state to the URL so `/zoekresultaten?...` is deep-linkable and
// seeds the page on entry (the E3 header→page handoff lands on it). Unknown/blank URL values are
// ignored, exactly like the Java `parseEnumCsv` + `fromUrl`.
//
// The url-value codes are byte-for-byte the Jmix `SearchFilters` enum url-values (`animal`, `dog`,
// `in_care`, `person`, `eigenaar`, `open`, `name_az`, …) and match the E1 classification codes the
// `GET /api/search` hits carry — so the SPA filters/round-trips without re-deriving anything.
//
/**
 * De periodes van de "Periode"-knop. In Jmix is die knop een dood element — een Div met een pijltje
 * en geen enkele klik-afhandeling — maar de operator kent hem van acc en verwacht dat hij er staat.
 * Hier filtert hij echt, op de opnamedatum die elke treffer al meelevert.
 */
export const PERIODS = ["all", "week", "month", "year"] as const;
export type Period = (typeof PERIODS)[number];

// Historische noot: de `period`-pil was in Jmix een dode knop, en werd daarom eerst niet gebouwd
// (Decision MOCKS). Hij staat er nu wel, maar dan werkend: alleen dieren dragen een opnamedatum, dus
// hij dunt de dierentreffers uit en laat de andere soorten met rust. Al het overige gaat 1:1 met Jmix.

/** Result-type / tab codes (Jmix `ResultType.urlValue`). Empty set = all types ("Alles"). */
export const RESULT_TYPES = ["animal", "relation", "invoice", "stay"] as const;
export type ResultType = (typeof RESULT_TYPES)[number];

/** Animal-species facet codes (Jmix `SpeciesFilter.urlValue`). */
export const SPECIES = ["dog", "cat", "rabbit", "rodent", "bird", "other"] as const;
export type Species = (typeof SPECIES)[number];

/**
 * Animal-status facet codes (Jmix `AnimalStatusFilter.urlValue`). `foster` is a valid enum but the
 * classifier never produces it (Jmix `mapVertrekSoortToStatus` only yields placed/deceased/checked_out)
 * — the facet row is still rendered (count 0), matching the Jmix source.
 */
export const ANIMAL_STATUSES = ["in_care", "foster", "placed", "checked_out", "deceased"] as const;
export type AnimalStatus = (typeof ANIMAL_STATUSES)[number];

/** Relation-type facet codes (Jmix `RelationTypeFilter.urlValue`). */
export const RELATION_TYPES = ["person", "organization"] as const;
export type RelationType = (typeof RELATION_TYPES)[number];

/** Relation-role facet codes (Jmix `RelationRoleFilter.urlValue`). */
export const RELATION_ROLES = [
  "eigenaar",
  "vinder",
  "brenger",
  "pleeggezin",
  "donateur",
  "dierenambulance",
  "dierenartspraktijk",
  "leverancier",
] as const;
export type RelationRole = (typeof RELATION_ROLES)[number];

/** Invoice-status facet codes (Jmix `InvoiceStatusFilter.urlValue`). */
export const INVOICE_STATUSES = ["open", "paid", "expired", "credited", "cancelled"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/** Sort codes (Jmix `SortOrder.urlValue`); `relevance` is the default (omitted from the URL). */
export const SORT_ORDERS = ["relevance", "newest", "oldest", "name_az"] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 50;

/** Immutable filter state for the results page — the TS shape of the Jmix `SearchFilters` DTO. */
export interface SearchFilters {
  query: string;
  /** Order-preserving; empty = all types. */
  resultTypes: ResultType[];
  species: Species[];
  animalStatuses: AnimalStatus[];
  relationTypes: RelationType[];
  relationRoles: RelationRole[];
  invoiceStatuses: InvoiceStatus[];
  sort: SortOrder;
  /** Opnamedatum vanaf: alles, deze week, deze maand, dit jaar. Zie PERIODS. */
  period: Period;
  page: number;
  pageSize: number;
  minDaysPresent: number;
}

/** A blank, all-defaults filter state (Jmix `SearchFilters.builder().build()`). */
export function emptyFilters(): SearchFilters {
  return {
    query: "",
    resultTypes: [],
    species: [],
    animalStatuses: [],
    relationTypes: [],
    relationRoles: [],
    invoiceStatuses: [],
    sort: "relevance",
    period: "all",
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    minDaysPresent: 0,
  };
}

/**
 * Parse a CSV of codes into a canonical-ordered list (Jmix uses an `EnumSet`, so order follows the enum
 * declaration regardless of the URL order). Unknown/blank codes are dropped.
 */
function parseCsv<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  if (!raw) return [];
  const present = new Set(raw.split(",").map((p) => p.trim().toLowerCase()));
  return allowed.filter((c) => present.has(c));
}

/** Order a code list by its canonical declaration (mirrors the Jmix EnumSet iteration order). */
function ordered<T extends string>(list: readonly T[], allowed: readonly T[]): T[] {
  return allowed.filter((c) => list.includes(c));
}

function parseIntOr(raw: string | null, def: number): number {
  if (raw == null) return def;
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isNaN(n) ? def : n;
}

function clampPageSize(n: number): number {
  return Math.min(MAX_PAGE_SIZE, Math.max(1, n));
}

/** Build filters from a URL query string (Jmix `SearchFilters.fromUrlParams`). Tolerates a leading `?`. */
export function fromSearch(search: string): SearchFilters {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const sortRaw = (params.get("sort") ?? "").toLowerCase();
  const sort = (SORT_ORDERS as readonly string[]).includes(sortRaw)
    ? (sortRaw as SortOrder)
    : "relevance";
  return {
    query: (params.get("q") ?? "").trim(),
    resultTypes: parseCsv(params.get("type"), RESULT_TYPES),
    species: parseCsv(params.get("species"), SPECIES),
    animalStatuses: parseCsv(params.get("animalStatus"), ANIMAL_STATUSES),
    relationTypes: parseCsv(params.get("relationType"), RELATION_TYPES),
    relationRoles: parseCsv(params.get("relationRole"), RELATION_ROLES),
    invoiceStatuses: parseCsv(params.get("invoiceStatus"), INVOICE_STATUSES),
    sort,
    period: (PERIODS as readonly string[]).includes(params.get("period") ?? "")
      ? ((params.get("period") as Period))
      : "all",
    page: Math.max(1, parseIntOr(params.get("page"), 1)),
    pageSize: clampPageSize(parseIntOr(params.get("pageSize"), DEFAULT_PAGE_SIZE)),
    minDaysPresent: Math.max(0, parseIntOr(params.get("minDaysPresent"), 0)),
  };
}

/**
 * Serialize to a URL query string without a leading `?` (Jmix `SearchFilters.toQueryString`). Empty /
 * default values are omitted; keys are emitted in the Jmix `TreeMap` order (alphabetical) so the URL is
 * stable and matches the Jmix-generated links. `q` is `encodeURIComponent`-encoded (matching the E3
 * header handoff); the csv code lists are safe identifiers, left unencoded like Jmix.
 */
export function toQueryString(f: SearchFilters): string {
  const out: Array<[string, string]> = [];
  if (f.query) out.push(["q", encodeURIComponent(f.query)]);
  if (f.resultTypes.length) out.push(["type", ordered(f.resultTypes, RESULT_TYPES).join(",")]);
  if (f.species.length) out.push(["species", ordered(f.species, SPECIES).join(",")]);
  if (f.animalStatuses.length) out.push(["animalStatus", ordered(f.animalStatuses, ANIMAL_STATUSES).join(",")]);
  if (f.relationTypes.length) out.push(["relationType", ordered(f.relationTypes, RELATION_TYPES).join(",")]);
  if (f.relationRoles.length) out.push(["relationRole", ordered(f.relationRoles, RELATION_ROLES).join(",")]);
  if (f.invoiceStatuses.length) out.push(["invoiceStatus", ordered(f.invoiceStatuses, INVOICE_STATUSES).join(",")]);
  if (f.sort !== "relevance") out.push(["sort", f.sort]);
  if (f.period !== "all") out.push(["period", f.period]);
  if (f.page > 1) out.push(["page", String(f.page)]);
  if (f.pageSize !== DEFAULT_PAGE_SIZE) out.push(["pageSize", String(f.pageSize)]);
  if (f.minDaysPresent > 0) out.push(["minDaysPresent", String(f.minDaysPresent)]);
  out.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return out.map(([k, v]) => `${k}=${v}`).join("&");
}

/** True when any domain (non result-type) filter is active — drives the facets-empty empty state. */
export function hasAnyDomainFilter(f: SearchFilters): boolean {
  return (
    f.species.length > 0 ||
    f.animalStatuses.length > 0 ||
    f.relationTypes.length > 0 ||
    f.relationRoles.length > 0 ||
    f.invoiceStatuses.length > 0 ||
    f.minDaysPresent > 0
  );
}

/** Toggle a value in a code list, preserving the canonical order of `allowed`. */
export function toggleCode<T extends string>(list: T[], value: T, allowed: readonly T[]): T[] {
  const has = list.includes(value);
  const next = has ? list.filter((x) => x !== value) : [...list, value];
  return allowed.filter((c) => next.includes(c));
}
